import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../context/AuthContext'
import Navbar from '../../components/Navbar'
import Footer from '../../components/Footer'
import LoadingSpinner from '../../components/LoadingSpinner'

// ============================================================
// PAYMENT PROCESSOR — swap this function when adding real gateway
// Currently simulates a successful payment
// To integrate Paystack/Stripe/PayPal:
//   1. Install their SDK
//   2. Replace this function with their checkout call
//   3. Return { success: true, reference: theirRef } on success
// ============================================================
const processPayment = async ({ amount, method, memberName, email }) => {
  // Simulate network delay
  await new Promise(resolve => setTimeout(resolve, 1500))
  // Always succeeds in simulation mode
  return {
    success: true,
    reference: `SIM-${Date.now()}`,
  }
}

const CLUB_ID = 'a0000000-0000-0000-0000-000000000001'

export default function Subscribe() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [supporter, setSupporter] = useState(null)
  const [settings, setSettings] = useState({})
  const [loading, setLoading] = useState(true)
  const [processing, setProcessing] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState(null)
  const [selectedMethod, setSelectedMethod] = useState('mobile_money')
  const [reference, setReference] = useState(null)

  useEffect(() => {
    if (!user) { navigate('/login'); return }
    const fetchData = async () => {
      const { data: supporterData } = await supabase
        .from('supporters').select('*, chapters(name), clubs(name)')
        .eq('id', user.id).single()

      const { data: settingsData } = await supabase
        .from('settings').select('key, value')
        .in('key', ['member_subscription_fee', 'currency', 'season_name', 'season_end_date'])

      const mapped = {}
      settingsData?.forEach(s => mapped[s.key] = s.value)

      // Check if already paid
      const { data: sub } = await supabase
        .from('subscriptions').select('valid_until')
        .eq('supporter_id', user.id)
        .order('valid_until', { ascending: false })
        .limit(1).single()

      if (sub && new Date(sub.valid_until) >= new Date()) {
        navigate('/dashboard')
        return
      }

      setSupporter(supporterData)
      setSettings(mapped)
      setLoading(false)
    }
    fetchData()
  }, [user, navigate])

  const handlePayment = async () => {
    setProcessing(true)
    setError(null)

    try {
      const amount = Number(settings.member_subscription_fee || 50)

      // Step 1 — process payment (swap this for real gateway later)
      const result = await processPayment({
        amount,
        method: selectedMethod,
        memberName: supporter.full_name,
        email: user.email,
      })

      if (!result.success) {
        setError('Payment failed. Please try again.')
        setProcessing(false)
        return
      }

      // Step 2 — record subscription in Supabase
      const validUntil = settings.season_end_date || (() => {
        const d = new Date()
        d.setFullYear(d.getFullYear() + 1)
        return d.toISOString().split('T')[0]
      })()

      const { error: subError } = await supabase.from('subscriptions').insert({
        supporter_id: user.id,
        club_id: CLUB_ID,
        amount,
        payment_date: new Date().toISOString().split('T')[0],
        valid_until: validUntil,
        status: 'active',
        payment_method: selectedMethod,
        transaction_ref: result.reference,
      })

      if (subError) {
        setError('Payment processed but failed to record. Please contact support.')
        setProcessing(false)
        return
      }

      setReference(result.reference)
      setSuccess(true)
    } catch (err) {
      setError('Something went wrong. Please try again.')
    }
    setProcessing(false)
  }

  if (loading) return <LoadingSpinner fullScreen message="Loading payment details..." />

  const amount = Number(settings.member_subscription_fee || 50)
  const currency = settings.currency || 'ZMW'

  const paymentMethods = [
    { id: 'mobile_money', label: 'Mobile Money', icon: '📱', desc: 'MTN, Airtel, Zamtel' },
    { id: 'card', label: 'Card', icon: '💳', desc: 'Visa, Mastercard' },
    { id: 'bank_transfer', label: 'Bank Transfer', icon: '🏦', desc: 'Direct bank payment' },
    { id: 'cash', label: 'Cash', icon: '💵', desc: 'Pay in person to coordinator' },
  ]

  return (
    <div className="min-h-screen bg-white dark:bg-black text-gray-900 dark:text-white flex flex-col">
      <Navbar />
      <div className="flex-1 flex flex-col items-center justify-center px-4 sm:px-8 py-10 sm:py-16">
        <div className="w-full max-w-md">

          {success ? (
            /* Success State */
            <div className="text-center">
              <div className="w-20 h-20 rounded-full bg-green-100 dark:bg-green-900/30 border-2 border-green-500 flex items-center justify-center mx-auto mb-6">
                <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="text-green-600 dark:text-green-400">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              </div>
              <h1 className="text-2xl font-black uppercase tracking-tight mb-2 text-gray-900 dark:text-white">
                Payment Successful!
              </h1>
              <p className="text-gray-500 text-sm mb-2">
                Your membership is now active for {settings.season_name || 'this season'}.
              </p>
              {reference && (
                <p className="text-gray-400 text-xs font-mono mb-8">
                  Ref: {reference}
                </p>
              )}
              <div className="flex flex-col gap-3">
                <button
                  onClick={() => navigate('/membership-card')}
                  className="w-full bg-green-600 hover:bg-green-700 text-white py-3 font-bold uppercase tracking-widest text-sm transition-colors"
                >
                  View My Card
                </button>
                <button
                  onClick={() => navigate('/dashboard')}
                  className="w-full border border-gray-200 dark:border-white/10 hover:border-green-600 text-gray-500 dark:text-gray-400 hover:text-green-600 dark:hover:text-white py-3 font-bold uppercase tracking-widest text-sm transition-colors"
                >
                  Go to Dashboard
                </button>
              </div>
            </div>

          ) : (
            /* Payment Form */
            <>
              <div className="mb-8">
                <div className="text-xs text-green-600 dark:text-green-400 uppercase tracking-widest font-semibold mb-2">
                  Membership Subscription
                </div>
                <h1 className="text-2xl sm:text-3xl font-black uppercase tracking-tight mb-2 text-gray-900 dark:text-white">
                  Activate Your Membership
                </h1>
                <p className="text-gray-500 text-sm">
                  {settings.season_name || 'Current Season'} · {supporter?.clubs?.name}
                </p>
              </div>

              {/* Member Summary */}
              <div className="bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 p-4 mb-6 flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-green-900/50 border-2 border-green-700/50 flex items-center justify-center text-white font-black shrink-0">
                  {supporter?.full_name?.split(' ').map(n => n[0]).join('').toUpperCase()}
                </div>
                <div className="min-w-0">
                  <div className="font-black text-gray-900 dark:text-white text-sm uppercase tracking-tight truncate">
                    {supporter?.full_name}
                  </div>
                  <div className="text-gray-500 text-xs mt-0.5">
                    {supporter?.membership_id} · {supporter?.chapters?.name}
                  </div>
                </div>
              </div>

              {/* Amount */}
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800/50 p-6 mb-6 text-center">
                <div className="text-xs text-green-600 dark:text-green-400 uppercase tracking-widest font-semibold mb-2">
                  Season Subscription Fee
                </div>
                <div className="text-4xl font-black text-green-600 dark:text-green-400">
                  {currency} {amount.toLocaleString()}
                </div>
                {settings.season_end_date && (
                  <div className="text-gray-500 text-xs mt-2">
                    Valid until {new Date(settings.season_end_date).toLocaleDateString('en-US', {
                      month: 'long', day: 'numeric', year: 'numeric'
                    })}
                  </div>
                )}
              </div>

              {/* Payment Method */}
              <div className="mb-6">
                <div className="text-xs text-gray-500 uppercase tracking-widest font-semibold mb-3">
                  Select Payment Method
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {paymentMethods.map((method) => (
                    <button
                      key={method.id}
                      onClick={() => setSelectedMethod(method.id)}
                      className={`p-4 border text-left transition-colors ${
                        selectedMethod === method.id
                          ? 'border-green-600 bg-green-50 dark:bg-green-900/20'
                          : 'border-gray-200 dark:border-white/10 hover:border-green-600/50'
                      }`}
                    >
                      <div className="text-xl mb-2">{method.icon}</div>
                      <div className="font-bold text-sm text-gray-900 dark:text-white">{method.label}</div>
                      <div className="text-gray-400 text-xs mt-0.5">{method.desc}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Cash notice */}
              {selectedMethod === 'cash' && (
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800/50 px-4 py-3 text-sm mb-4">
                  <div className="text-blue-600 dark:text-blue-400 font-bold text-xs uppercase tracking-widest mb-1">
                    Cash Payment
                  </div>
                  <p className="text-blue-700/80 dark:text-blue-200/70 text-xs leading-relaxed">
                    Pay {currency} {amount.toLocaleString()} in person to your chapter coordinator. Click confirm below and your coordinator will verify and activate your membership.
                  </p>
                </div>
              )}

              {/* Simulation notice */}
              <div className="bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 px-4 py-3 text-xs text-gray-400 mb-6">
                🔧 <span className="font-semibold">Demo Mode</span> — Payments are simulated. Real payment gateway coming soon.
              </div>

              {error && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 text-red-600 dark:text-red-400 px-4 py-3 text-sm mb-4">
                  {error}
                </div>
              )}

              <button
                onClick={handlePayment}
                disabled={processing}
                className="w-full bg-green-600 hover:bg-green-700 disabled:bg-green-900 disabled:text-green-700 text-white py-4 font-bold uppercase tracking-widest text-sm transition-colors"
              >
                {processing ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                    </svg>
                    Processing...
                  </span>
                ) : `Pay ${currency} ${amount.toLocaleString()}`}
              </button>
            </>
          )}
        </div>
      </div>
      <Footer />
    </div>
  )
}